import get from 'lodash/get';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmployeeTypes } from '@constants';
import { EmployeesService, SelectOption, useDebounce } from '@innowise-group/core';
import { AvatarItem, DropdownListItem, Input } from '@innowise-group/mui-kit';
import { localizedNameObject } from '@innowise-group/utilities';

import { EmployeeSelect } from './employee-field.styles';

const renderEmployeeListItem = (props, value: SelectOption, { selected }) => {
  return (
    <DropdownListItem {...props} key={value.value} selected={selected}>
      <AvatarItem photoId={value.miniPhotoUrl} miniPhotoUrl={value.miniPhotoHrmUrl} name={value.title} isOption />
    </DropdownListItem>
  );
};

interface EmployeeFieldProps {
  parentField?: string;
  field: string;
  label: string;
  placeholder: string;
  disabled?: boolean;
  disableClearable?: boolean;
  showErrors?: boolean;
  onlyActual?: boolean;
  employeeType?: EmployeeTypes;
  defaultPopperBehavior?: boolean;
  defaultOptions?: SelectOption[];
}

const EmployeeField: React.FC<EmployeeFieldProps> = ({
  parentField,
  field,
  label,
  placeholder,
  disabled,
  disableClearable,
  employeeType,
  showErrors,
  onlyActual,
  defaultPopperBehavior,
  defaultOptions,
}) => {
  const { t } = useTranslation();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [employeesOptions, setEmployeesOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [employeeValue, setEmployeeValue] = useState<string>('');

  const [debouncedEmployeeValue] = useDebounce<string>(employeeValue, 500);

  const fieldName = parentField ? `${parentField}.${field}Id` : `${field}Id`;
  const currentValue = watch(fieldName);
  const fullName = parentField ? `${parentField}.${field}FullName` : `${field}FullName`;
  const currentTitle = watch(fullName);
  const error = get(errors, fieldName);

  useEffect(() => {
    if (currentValue && currentTitle) {
      setEmployeeValue(currentTitle);
    }
  }, [currentValue, currentTitle]);

  useEffect(() => {
    if (!debouncedEmployeeValue && defaultOptions) {
      setEmployeesOptions(defaultOptions);
    } else {
      handleSearchEmployees(debouncedEmployeeValue);
    }
  }, [debouncedEmployeeValue, defaultOptions]);

  const handleSearchEmployees = async (value: string) => {
    setIsLoading(true);

    const response = await EmployeesService.searchEmployees({
      partOfName: value,
      employeeType: employeeType || EmployeeTypes.HRM,
      onlyActual,
    });

    setEmployeesOptions(
      (response.data.content || []).map((item) => {
        return {
          title: localizedNameObject(item).localizedFullName,
          value: item.employeeId.toString(),
          miniPhotoUrl: item.photoId?.toString() || null,
          miniPhotoHrmUrl: item.miniPhotoHrmUrl || null,
        };
      }),
    );

    setIsLoading(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => setEmployeeValue(event.target.value);

  const transformValue = (value: string) => {
    return employeesOptions?.length ? employeesOptions.find((item) => item.value === value?.toString())?.title : '';
  };

  const handleChange = (onChange: (value: string) => void) => (value: string) => {
    if (!value) setEmployeeValue('');

    onChange(value);
    setValue(fullName, transformValue(value));
  };

  const handleBlur = () => {
    if (currentValue) return;

    setEmployeeValue('');
  };

  const slotProps = useMemo(() => {
    if (defaultPopperBehavior) {
      return {
        popper: {
          sx: {
            zIndex: 1500,
          },
        },
      };
    }

    return undefined;
  }, [defaultPopperBehavior]);

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange } }) => {
        return (
          <EmployeeSelect
            value={currentValue}
            onChange={handleChange(onChange)}
            onBlur={handleBlur}
            disableClearable={disableClearable || !currentValue}
            options={employeesOptions || []}
            renderOption={renderEmployeeListItem}
            loading={isLoading}
            fullWidth
            disabled={disabled}
            slotProps={slotProps}
            disablePortal={defaultPopperBehavior ? false : true}
            renderInput={(params) => {
              return (
                <Input
                  {...params}
                  fullWidth
                  error={showErrors && !!error}
                  helperText={showErrors && error?.message}
                  onChange={handleInputChange}
                  label={t(label)}
                  placeholder={t(placeholder)}
                />
              );
            }}
          />
        );
      }}
    />
  );
};

export default React.memo(EmployeeField);
